import { useState, useEffect, useCallback, useMemo } from 'react';
import { createCustomerETLApi } from '../api/etlServiceApi';

// ── Data source definitions ──────────────────────────────────────────────────

const SOURCES = [
  {
    id: 'produktionsauftraege',
    label: 'Belegkette',
    description: 'Production order master data. Also carries Rechnung_Nr/Auftragsnummer columns used to link production orders to invoices and orders.',
    produces: ['production order objects', 'object-to-object relationships'],
    expectedColumns: [
      'pa_nummer', 'Kundenname', 'PAGesamtpreis_AB', 'KampagnenNr', 'Kampagnen_Soll_Start',
      'Kampagnen_Soll_Ende', 'PA_Fertig', 'MengeKG', 'GelieferteMenge_m', 'MengeVorrohrKG',
      'MengeLager3_kg', 'MengeLager3_m', 'Bestellmenge_kg', 'Bestellmenge_m', 'Bestellmenge_Stk',
      'GezogeneMeter_Total', 'Hauptnutzungszeit (s)', 'Gesamtdurchlaufzeit_Produktion (min)',
      'AnzahlZuege', 'PA_Termin', 'Werkstoff', 'Werkstoffgruppe', 'Bestaetigter_Termin',
      'PA_Erstellungszeitpunkt', 'Rechnung_Nr', 'Auftragsnummer',
    ],
  },
  {
    id: 'produktionsauftraege_completed',
    label: 'Produktionsaufträge (Completed)',
    description: 'Production order lifecycle events (created, completed).',
    produces: ['production order created', 'production order completed'],
    expectedColumns: ['pa_nummer', 'pa_fertig', 'AbgeschlossenAm', 'pa_erstellt'],
  },
  {
    id: 'bde_zeiten',
    label: 'BDE-Zeiten',
    description: 'Machine operation times — P1 (AG0010), P2 (AG0020), P3 (AG0030).',
    produces: ['P1 start', 'P1 end', 'P2 start', 'P2 end', 'P3 start', 'P3 end'],
    expectedColumns: ['PANummer', 'AG', 'zt_von', 'zt_bis'],
  },
  {
    id: 'zuege_report',
    label: 'Züge-Report',
    description: 'Drawing-machine cycle records from the Ethen HMI system.',
    produces: ['cycle_start', 'cycle_end'],
    expectedColumns: ['Startzeit', 'RowCreateTime', 'PANummer', 'LaufendeNrZug', 'Nummer', 'GezogeneMeter', 'Ziehbank', 'dauer', 'geschw', 'UmformGrad', 'Zugfrequenz'],
  },
  {
    id: 'angebote',
    label: 'Angebote',
    description: 'Offer master data.',
    produces: ['offer objects'],
    expectedColumns: ['Angebotsnummer', 'Gueltig_bis', 'Angebotswert_Netto', 'Kundenname', 'Angebotsstatus', 'Angebotsdatum', 'Verknuepfter_Auftrag'],
  },
  {
    id: 'angebote_events',
    label: 'Angebote (Events)',
    description: 'Offer lifecycle events — creation and (Archiv-only) printing.',
    produces: ['Angebot angelegt', 'Angebot gedruckt'],
    expectedColumns: ['Angebotsnummer', 'Angebotsdatum', 'Herkunft', 'Angebotsdruck'],
  },
  {
    id: 'rechnung',
    label: 'Rechnungen',
    description: 'Invoice master data (dedicated invoice object source).',
    produces: ['invoice objects'],
    expectedColumns: ['Rechnung_Nr', 'Kundenname', 'Rechnung_WarenwertNetto', 'Rechnungsdruck_Zeitpunkt'],
  },
  {
    id: 'rechnung_events',
    label: 'Rechnungen (Events)',
    description: 'Invoice lifecycle events — creation and (Archiv-only) printing, the latter also linking back to the production order.',
    produces: ['Rechnung erstellt', 'Rechnung gedruckt'],
    expectedColumns: ['Rechnung_Nr', 'Rechnung_Datum', 'Herkunft', 'Rechnungsdruck_Zeitpunkt'],
  },
  {
    id: 'auftraege',
    label: 'Aufträge',
    description: 'Order master data.',
    produces: ['order objects'],
    expectedColumns: ['Auftragsnummer', 'Auftrag_WarenwertNetto', 'Kundenname', 'Bestaetigter_Termin', 'Auftragsdatum'],
  },
  {
    id: 'auftraege_events',
    label: 'Aufträge (Events)',
    description: 'Order lifecycle events — creation (also linking back to the originating offer via Angebotsnummer) and (Archiv-only) confirmation printing (linking back to the production order).',
    produces: ['Auftrag angelegt', 'Auftrag gedruckt'],
    expectedColumns: ['Auftragsnummer', 'Angebotsnummer', 'Auftragsdatum', 'Herkunft', 'Auftragsbestaetigung_Druck'],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Build a DuckDB SELECT from an alias, its actual columns, and a mapping dict
 * { expectedColName: actualColName }. Mapped columns get an AS alias.
 */
function buildSql(alias, allColumns, mappings) {
  if (!alias || !allColumns?.length) return `SELECT * FROM "erp"."${alias}"`;
  const selectParts = allColumns.map(col => {
    const expectedAlias = Object.entries(mappings).find(([, act]) => act === col)?.[0];
    return expectedAlias && expectedAlias !== col
      ? `"${col}" AS "${expectedAlias}"`
      : `"${col}"`;
  });
  return `SELECT\n  ${selectParts.join(',\n  ')}\nFROM "erp"."${alias}"`;
}

/**
 * Compute match status for each expected column against actual columns.
 * Returns { status: { col: "matched"|"case"|"missing" }, autoMappings: { col: actualCol } }
 */
function resolveColumns(expectedCols, actualCols) {
  const status = {};
  const autoMappings = {};
  for (const exp of expectedCols) {
    if (actualCols.includes(exp)) {
      status[exp] = 'matched';
    } else {
      const caseMatch = actualCols.find(c => c.toLowerCase() === exp.toLowerCase());
      if (caseMatch) {
        status[exp] = 'case';
        autoMappings[exp] = caseMatch;
      } else {
        status[exp] = 'missing';
      }
    }
  }
  return { status, autoMappings };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TestResultPanel({ result }) {
  if (!result) return null;
  if (result.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
        <p className="text-xs font-semibold text-red-700 mb-1">Query Error</p>
        <p className="text-xs text-red-600 font-mono whitespace-pre-wrap">{result.error}</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="font-medium text-emerald-700">
          {result.row_count?.toLocaleString()} rows returned
        </span>
        {result.execution_time_ms != null && <span>{result.execution_time_ms.toFixed(1)} ms</span>}
        <span>{result.columns?.length} columns</span>
      </div>
      {result.rows?.length > 0 && (
        <div className="overflow-auto max-h-[180px] border border-gray-200 rounded-lg">
          <table className="text-xs min-w-full">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                {result.columns.map(col => (
                  <th key={col} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap border-b border-gray-200">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {result.rows.slice(0, 5).map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {result.columns.map(col => (
                    <td key={col} className="px-3 py-1.5 text-gray-700 font-mono whitespace-nowrap max-w-[200px] truncate" title={String(row[col] ?? '')}>
                      {row[col] == null ? <span className="text-gray-300 italic">null</span> : String(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Source card ───────────────────────────────────────────────────────────────

function SourceCard({
  source,
  selectedAlias,
  onAliasChange,
  columnMappings,
  onColumnMappingsChange,
  tableColumns,
  onFetchColumns,
  extractionAliases,
  etlServiceReachable,
  onTestWarehouseQuery,
}) {
  const [expanded, setExpanded] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Resolve column status when alias + columns are available
  const { colStatus, effectiveMappings } = useMemo(() => {
    if (!selectedAlias || !tableColumns) return { colStatus: {}, effectiveMappings: {} };
    const { status, autoMappings } = resolveColumns(source.expectedColumns, tableColumns);
    const merged = { ...autoMappings, ...columnMappings };
    return { colStatus: status, effectiveMappings: merged };
  }, [selectedAlias, tableColumns, columnMappings, source.expectedColumns]);

  const handleAliasSelect = (alias) => {
    onAliasChange(alias || null);
    onColumnMappingsChange({});
    setTestResult(null);
    if (alias) onFetchColumns(alias);
  };

  const handleTest = useCallback(async () => {
    if (!selectedAlias || !onTestWarehouseQuery) return;
    setTesting(true);
    setTestResult(null);
    const sql = `SELECT * FROM "erp"."${selectedAlias}" LIMIT 5`;
    try {
      const res = await onTestWarehouseQuery(sql);
      setTestResult(res.success ? res : { error: res.error ?? 'Query failed' });
    } catch (err) {
      setTestResult({ error: err?.response?.data?.detail ?? err.message ?? 'Request failed' });
    } finally {
      setTesting(false);
    }
  }, [selectedAlias, onTestWarehouseQuery]);

  // Missing column count for header badge
  const missingCount = useMemo(() => {
    if (!selectedAlias || !tableColumns) return null;
    return source.expectedColumns.filter(c => {
      const st = colStatus[c];
      if (st === 'matched') return false;
      if (st === 'case' || st === 'missing') return !effectiveMappings[c];
      return true;
    }).length;
  }, [selectedAlias, tableColumns, colStatus, effectiveMappings, source.expectedColumns]);

  const selectedAliasInfo = extractionAliases.find(a => a.alias === selectedAlias);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
          <span className="text-sm font-semibold text-gray-800">{source.label}</span>
          <span className="text-xs text-gray-400 hidden sm:inline">{source.description}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {missingCount != null && missingCount > 0 && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-600">
              {missingCount} missing col{missingCount !== 1 ? 's' : ''}
            </span>
          )}
          {missingCount === 0 && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
              Columns OK
            </span>
          )}
          {!selectedAlias && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
              No source mapped
            </span>
          )}
          <div className="flex flex-wrap gap-1 max-w-[240px]">
            {source.produces.map(p => (
              <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 border border-violet-200 text-violet-700 font-medium">
                {p}
              </span>
            ))}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="p-5 space-y-4">
          {/* Alias dropdown */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600">
              Extracted Source
              <span className="ml-1 text-gray-400 font-normal">— select which extraction source maps to this OCEL input</span>
            </label>

            {extractionAliases.length === 0 ? (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                No extraction sources defined. Go to the <strong>Data Sources</strong> tab to define SQL Server queries first.
              </div>
            ) : (
              <select
                value={selectedAlias ?? ''}
                onChange={e => handleAliasSelect(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-violet-400 text-gray-800"
              >
                <option value="">— select an extraction source —</option>
                {extractionAliases.map(a => (
                  <option key={a.alias} value={a.alias}>
                    {a.alias}{a.description ? ` — ${a.description}` : ''}{a.rows != null ? ` (${a.rows.toLocaleString()} rows)` : ''}
                  </option>
                ))}
              </select>
            )}

            {selectedAlias && selectedAliasInfo?.rows == null && (
              <p className="text-[11px] text-amber-600">
                Table <code className="font-mono">erp.{selectedAlias}</code> not found in DuckDB — run Step 1 (Extract) first.
              </p>
            )}
          </div>

          {/* Column mapping — only shown once alias is selected and columns are loaded */}
          {selectedAlias && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-600">Column Mapping</p>
              {!tableColumns ? (
                <p className="text-xs text-gray-400 italic">Loading columns…</p>
              ) : tableColumns.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No columns found — table may be empty or not yet extracted.</p>
              ) : (
                <div className="space-y-2">
                  {source.expectedColumns.map(expCol => {
                    const st = colStatus[expCol];
                    const currentMapping = effectiveMappings[expCol];

                    return (
                      <div key={expCol} className="flex items-center gap-3 min-h-[28px]">
                        <code className="text-xs font-mono text-gray-700 w-52 flex-shrink-0 truncate" title={expCol}>
                          {expCol}
                        </code>

                        {st === 'matched' && (
                          <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                            </svg>
                            auto-matched
                          </span>
                        )}

                        {(st === 'case' || st === 'missing') && (
                          <div className="flex items-center gap-2">
                            <select
                              value={currentMapping ?? ''}
                              onChange={e => {
                                const val = e.target.value;
                                const updated = { ...columnMappings };
                                if (val) updated[expCol] = val;
                                else delete updated[expCol];
                                onColumnMappingsChange(updated);
                              }}
                              className={`text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 text-gray-700 ${
                                st === 'case'
                                  ? 'border-amber-200 bg-amber-50 focus:ring-amber-400'
                                  : 'border-red-200 bg-red-50 focus:ring-red-400'
                              }`}
                            >
                              <option value="">— map to column —</option>
                              {tableColumns.map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            <span className={`text-[10px] ${st === 'case' ? 'text-amber-500' : 'text-red-400'}`}>
                              {st === 'case' ? 'case mismatch' : 'not found'}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Generated SQL preview */}
          {selectedAlias && tableColumns?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-600">Generated DuckDB Query</p>
              <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 overflow-auto max-h-[130px] text-gray-700 whitespace-pre-wrap">
                {buildSql(selectedAlias, tableColumns, effectiveMappings)}
              </pre>
            </div>
          )}

          {/* Test button */}
          {selectedAlias && (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleTest}
                  disabled={testing || !etlServiceReachable}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  {testing ? (
                    <>
                      <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Testing…
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                      </svg>
                      Test Query (DuckDB)
                    </>
                  )}
                </button>
                {testResult && (
                  <button onClick={() => setTestResult(null)} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                    Clear
                  </button>
                )}
              </div>
              <TestResultPanel result={testResult} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function OCELQueryConfig({ customerId, etlServiceReachable, extractionSourcesVersion }) {
  const api = useMemo(() => (customerId ? createCustomerETLApi(customerId) : null), [customerId]);
  // ocel_source_mappings: { source_id: { source_alias, column_mappings } }
  const [selectedAliases, setSelectedAliases] = useState(
    Object.fromEntries(SOURCES.map(s => [s.id, null]))
  );
  const [columnMappings, setColumnMappings] = useState(
    Object.fromEntries(SOURCES.map(s => [s.id, {}]))
  );
  const [tableColumnsCache, setTableColumnsCache] = useState(
    Object.fromEntries(SOURCES.map(s => [s.id, null]))
  );

  const [extractionAliases, setExtractionAliases] = useState([]);
  const [fullConfig, setFullConfig] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!etlServiceReachable || !api) { setLoading(false); return; }
    setLoading(true);

    Promise.all([
      api.getTransformerConfig().catch(() => null),
      api.getExtractionSourceAliases().catch(() => []),
    ])
      .then(([config, aliases]) => {
        setFullConfig(config);
        setExtractionAliases(aliases ?? []);
        setDateFrom(config?.date_from ?? '');
        setDateTo(config?.date_to ?? '');

        const newAliases = Object.fromEntries(SOURCES.map(s => [s.id, null]));
        const newMappings = Object.fromEntries(SOURCES.map(s => [s.id, {}]));

        // Load from ocel_source_mappings (new format)
        if (config?.ocel_source_mappings) {
          for (const [sid, mapping] of Object.entries(config.ocel_source_mappings)) {
            if (mapping?.source_alias) newAliases[sid] = mapping.source_alias;
            if (mapping?.column_mappings) newMappings[sid] = mapping.column_mappings;
          }
        }

        setSelectedAliases(newAliases);
        setColumnMappings(newMappings);

        // Pre-fetch columns for sources that already have an alias
        for (const sid of SOURCES.map(s => s.id)) {
          const alias = newAliases[sid];
          if (alias) {
            api.getTableColumns(alias)
              .then(res => {
                if (res?.columns) setTableColumnsCache(prev => ({ ...prev, [sid]: res.columns }));
              })
              .catch(() => {});
          }
        }
      })
      .catch(err => {
        setLoadError(err?.response?.data?.detail ?? err.message ?? 'Failed to load config');
      })
      .finally(() => setLoading(false));
  }, [etlServiceReachable, api, extractionSourcesVersion]);

  const fetchColumnsForSource = useCallback((sourceId, alias) => {
    if (!api) return;
    api.getTableColumns(alias)
      .then(res => {
        if (res?.columns) setTableColumnsCache(prev => ({ ...prev, [sourceId]: res.columns }));
      })
      .catch(() => {
        setTableColumnsCache(prev => ({ ...prev, [sourceId]: [] }));
      });
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    setSavedOk(false);
    setSaveError(null);
    try {
      // Build ocel_source_mappings from current state
      const ocelSourceMappings = {};
      const sources = {};

      for (const src of SOURCES) {
        const alias = selectedAliases[src.id];
        const maps = columnMappings[src.id] ?? {};
        const cols = tableColumnsCache[src.id];

        // Compute effective mappings (auto + user)
        let effectiveMaps = maps;
        if (alias && cols) {
          const { autoMappings } = resolveColumns(src.expectedColumns, cols);
          effectiveMaps = { ...autoMappings, ...maps };
        }

        if (alias) {
          ocelSourceMappings[src.id] = {
            source_alias: alias,
            column_mappings: maps,
          };
          sources[src.id] = {
            sql: buildSql(alias, cols ?? [], effectiveMaps),
            table: alias,
            column_mappings: maps,
            mode: 'simple',
          };
        } else {
          // No alias selected — keep whatever was in the full config (or use a blank placeholder)
          sources[src.id] = fullConfig?.sources?.[src.id] ?? {
            sql: `SELECT * FROM "erp"."${src.id}"`,
            table: null,
            column_mappings: {},
            mode: 'advanced',
          };
        }
      }

      const payload = {
        sources,
        // extraction_sources is intentionally omitted — this tab only edits
        // ocel_source_mappings/sources. Sending the untouched list back would
        // re-validate it against the backend's blank-alias/SQL check (see
        // ExtractionSourcesConfig.jsx) and fail this save for a problem that
        // belongs to the Data Sources tab.
        ocel_source_mappings: ocelSourceMappings,
        date_from: dateFrom || '',
        date_to: dateTo || '',
      };

      await api.saveTransformerConfig(payload);

      // Refresh full config so subsequent saves stay in sync
      const refreshed = await api.getTransformerConfig();
      setFullConfig(refreshed);
      setDateFrom(refreshed?.date_from ?? '');
      setDateTo(refreshed?.date_to ?? '');

      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch (err) {
      setSaveError(err?.response?.data?.detail ?? err.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!etlServiceReachable) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
        <p className="text-sm text-gray-500 font-medium">ETL service is offline</p>
        <p className="text-xs text-gray-400 mt-1">Start the ETL service to configure OCEL mapping.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="bg-violet-50 border border-violet-200 rounded-xl px-5 py-4">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-violet-800">OCEL Source Mapping</p>
            <p className="text-xs text-violet-600 mt-1">
              Map each of the 10 OCEL inputs to one of your extracted sources.
              Define sources first in the <strong>Data Sources</strong> tab, run <strong>Step 1 (Extract)</strong>,
              then come back here to map them.
            </p>
          </div>
        </div>
      </div>

      {/* Time range filter */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
        <p className="text-sm font-semibold text-gray-800">Event Time Range</p>
        <p className="text-xs text-gray-500 mt-1 mb-3">
          Optional — restrict every pipeline run (scheduled and manual) to events within this
          window. Leave both blank to include all available data.
        </p>
        <div className="flex items-center gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={dateFrom ? dateFrom.slice(0, 10) : ''}
              onChange={e => setDateFrom(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-800 focus:outline-none focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={dateTo ? dateTo.slice(0, 10) : ''}
              onChange={e => setDateTo(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-800 focus:outline-none focus:border-primary-500"
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="mt-4 text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
          Could not load saved config ({loadError}) — showing defaults.
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-white rounded-xl border border-gray-200 animate-pulse" />)}
        </div>
      ) : (
        <>
          {SOURCES.map(source => (
            <SourceCard
              key={source.id}
              source={source}
              selectedAlias={selectedAliases[source.id]}
              onAliasChange={val => {
                setSelectedAliases(prev => ({ ...prev, [source.id]: val }));
                setTableColumnsCache(prev => ({ ...prev, [source.id]: null }));
              }}
              columnMappings={columnMappings[source.id] ?? {}}
              onColumnMappingsChange={val => setColumnMappings(prev => ({ ...prev, [source.id]: val }))}
              tableColumns={tableColumnsCache[source.id]}
              onFetchColumns={(alias) => fetchColumnsForSource(source.id, alias)}
              extractionAliases={extractionAliases}
              etlServiceReachable={etlServiceReachable}
              onTestWarehouseQuery={api ? (sql) => api.queryWarehouse(sql) : null}
            />
          ))}

          {/* Save bar */}
          <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-gray-200 rounded-xl px-5 py-3 flex items-center justify-between shadow-sm">
            <div className="text-xs text-gray-500">
              Saved mappings are used by{' '}
              <code className="bg-gray-100 px-1 rounded text-gray-700">ethen_full</code>{' '}
              on every pipeline run.
            </div>
            <div className="flex items-center gap-3">
              {saveError && <span className="text-xs text-red-600">{saveError}</span>}
              {savedOk && <span className="text-xs text-emerald-600 font-medium">Saved!</span>}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Saving…
                  </>
                ) : 'Save Mappings'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
